// web/src/console/ReadonlyChat.tsx
import type { JSX } from "react";
import { useEffect, useRef } from "react";
import type { Turn } from "@shared/types";
import { Markdown } from "../components/chat/Markdown";
import { IconThumbUp, IconThumbDown } from "../components/icons";

function matches(t: Turn, key: string): boolean {
  return t.id === key || (t.role === "assistant" && t.responseId === key);
}

export function ReadonlyChat({ turns, focusKey }: { turns: Turn[]; focusKey: string | null }): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusKey === null) return;
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-focus="${CSS.escape(focusKey)}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("cc-turn-flash");
      const id = window.setTimeout(() => el.classList.remove("cc-turn-flash"), 2200);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [focusKey, turns]);

  return (
    <div className="cc-chat thread" ref={containerRef}>
      {turns.map((t) => {
        const focusAttr = focusKey !== null && matches(t, focusKey) ? focusKey : undefined;
        if (t.role === "user") {
          return (
            <div key={t.id} className="turn turn-user" {...(focusAttr ? { "data-focus": focusAttr } : {})}>
              {t.text.length > 0 && <div className="user-bubble">{t.text}</div>}
            </div>
          );
        }
        return (
          <div key={t.id} className="turn turn-assistant" {...(focusAttr ? { "data-focus": focusAttr } : {})}>
            <span className="msg-avatar" aria-hidden="true">环</span>
            <div className="msg-body">
              <div className="answer-direct"><Markdown>{t.directAnswer}</Markdown></div>
              {t.steps && t.steps.length > 0 && <ol className="answer-steps">{t.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>}
              {t.nextStep && <div className="next-step"><span className="next-step-label">下一步建议</span>{t.nextStep}</div>}
              {t.rating && (() => {
                const reasons =
                  t.ratingReasons && t.ratingReasons.length > 0
                    ? t.ratingReasons
                    : t.ratingReason
                      ? [t.ratingReason]
                      : [];
                return (
                  <div className="cc-rating">
                    <div className={`cc-rating-badge cc-rating-${t.rating === "up" ? "ok" : "err"}`}>
                      {t.rating === "up" ? <IconThumbUp size={13} /> : <IconThumbDown size={13} />}
                      {t.rating === "up" ? "用户点赞" : "用户点踩"}
                    </div>
                    {reasons.length > 0 && (
                      <div className="cc-rating-reasons">
                        {reasons.map((r) => (
                          <span key={r} className="cc-rating-chip">{r}</span>
                        ))}
                      </div>
                    )}
                    {t.ratingNote && <div className="cc-rating-note">“{t.ratingNote}”</div>}
                    {t.ratingScreenshot && (
                      <img
                        className="cc-rating-shot"
                        src={`data:${t.ratingScreenshot.mimeType};base64,${t.ratingScreenshot.data}`}
                        alt="用户截图"
                      />
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        );
      })}
    </div>
  );
}
